<script>
import SearchResults from '@phoenix/mixins/components/search/SearchResults';
import SearchResultsTabArticles from '@maison/components/search/SearchResultsTabArticles';
import SearchResultsTabProducts from '@maison/components/search/SearchResultsTabProducts';
import SearchResultsTabUserManuals from '@maison/components/search/SearchResultsTabUserManuals';
import SearchResultsTabServices from '@maison/components/search/SearchResultsTabServices';
import SearchResultsTabStores from '@maison/components/search/SearchResultsTabStores';
import { PhxIcon, PhxTabPanel, PhxTabsResponsive } from '@phx-styleguides/jlc';
import { rescue } from '@phoenix/helpers/common-helper';
import { SEARCH_MIN_TERM_LENGTH } from '@maison/helpers/search-helper';
import { mapActions } from 'vuex';

export default {
  components: {
    PhxIcon,
    PhxTabsResponsive,
    PhxTabPanel,
    SearchResultsTabArticles,
    SearchResultsTabProducts,
    SearchResultsTabUserManuals,
    SearchResultsTabServices,
    SearchResultsTabStores,
  },

  mixins: [SearchResults],

  provide() {
    return {
      hasFindYourFitWidget: this.hasFindYourFitWidget,
      scrollableActionsWidget: this.scrollableActionsWidget,
    };
  },

  props: {
    hasFindYourFitWidget: {
      type: Boolean,
      default: false,
    },
    scrollableActionsWidget: {
      type: Object,
      default: () => ({}),
    },
  },

  computed: {
    shouldDisplayResults() {
      return !this.isLoading && this.hasResults && this.query.length >= SEARCH_MIN_TERM_LENGTH;
    },

    orderedTabs() {
      return Object.entries(this.tabs)
        .sort(([, a], [, b]) => b.total - a.total)
        .sort(([a], [b]) => (a === 'userManuals') - (b === 'userManuals'))
        .map(([key]) => key);
    },
  },

  watch: {
    shouldDisplayResults: {
      handler(value) {
        if (value) {
          this.$nextTick(() => {
            Toolkit.stickyBar(this.$refs.stickyBar);
          });
        }
      },
      immediate: true,
    },

    isLoading: {
      async handler(value) {
        if (value) {
          return;
        }

        const tabQueryParam = new URLSearchParams(window.location.search).get('tab');

        const newTab =
          tabQueryParam && this.tabs[tabQueryParam]?.data?.length > 0
            ? tabQueryParam
            : this.orderedTabs.find((tab) => this.tabs[tab]?.data?.length > 0);

        if (newTab !== undefined && newTab !== this.currentTab) {
          await this.changeCurrentTab(this.tabId(newTab));
        }
      },
    },
  },

  methods: {
    ...mapActions('search', ['setHasInteractedWithResults']),

    tabLabel(tab) {
      return `${this.tabTitle(tab)} <em>${this.tabTotal(tab)}</em>`;
    },

    isTabDisabled(tab) {
      return this.tabs[tab].total === 0;
    },

    changeCurrentTabTracked(tab) {
      this.changeCurrentTab(tab);
      this.setHasInteractedWithResults();

      rescue(() => {
        const event = window.tracking?.getSearchPaginationTracking?.(tab);
        if (event) {
          window.tracking.pushEvent(event);
        }
      });
    },
  },
};
</script>

<template>
  <div v-if="!isLoading && shouldDisplayResults" ref="stickyBar" class="sticky-bar search-bar">
    <div class="sticky-bar-search-wrapper container sticky-bar-navigation--search">
      <div class="sticky-bar-search-wrapper__nav">
        <phx-tabs-responsive
          v-cloak
          id="search-results"
          layout="start"
          :responsive-layouts="[{ from: 'lg', layout: 'stretch' }]"
          :value="tabId(currentTab)"
          teleport-target="#teleport-target-search-results-content"
          class="sticky-bar__tabs"
          @change="changeCurrentTabTracked"
        >
          <template #start>
            <phx-icon icon="chevron-left-10" size="10" bidirectional />
          </template>

          <template #end>
            <phx-icon icon="chevron-10" size="10" bidirectional />
          </template>

          <phx-tab-panel
            v-for="tab in orderedTabs"
            :id="tabId(tab)"
            :key="tab"
            class="tab-panel--full"
            :label="tabLabel(tab)"
            :disabled="isTabDisabled(tab)"
          >
            <component :is="componentNameForTab(tab)" />
          </phx-tab-panel>
        </phx-tabs-responsive>
      </div>
    </div>
  </div>
</template>
